import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import PageHeader from './_components/PageHeader';

/* ---------- Icons ---------- */

const I = ({ d, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChatIcon = (p) => <I {...p} d="M4 5h16v11H8l-4 4V5z" />;
const SendIcon = (p) => <I {...p} d="M3 11l18-8-8 18-2-8-8-2z" />;

/* ---------- Helpers ---------- */

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const fmtDayLabel = (iso) => {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ---------- Message bubble ---------- */

const Bubble = ({ msg }) => {
  const mine = msg.sender === 'client';
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[80%]">
        <div
          className={`rounded-2xl px-4 py-2.5 text-[14px] leading-6 shadow-sm ${
            mine
              ? 'bg-[#0b2740] text-white rounded-br-md'
              : 'bg-white text-[#1a2b3c] border border-gray-200 rounded-bl-md'
          }`}
        >
          {msg.body}
        </div>
        <div className={`text-[10px] text-gray-400 mt-1 ${mine ? 'text-right' : 'text-left'}`}>
          {fmtTime(msg.createdAt)}
        </div>
      </div>
    </div>
  );
};

/* ---------- Page ---------- */

const FintechMessages = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const bodyRef = useRef(null);

  /* ---- Load profile + messages on mount ---- */
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const [profileRes, msgRes] = await Promise.all([
          api.get('/fintech/dashboard').catch(() => null),
          api.get('/fintech/messages'),
        ]);
        if (!alive) return;
        if (profileRes) setData(profileRes.data);
        setMessages(msgRes.data.messages || []);
      } catch (err) {
        console.error(err);
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => { alive = false; };
  }, []);

  /* ---- Poll for new messages every 10s ---- */
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const res = await api.get('/fintech/messages');
        setMessages(res.data.messages || []);
      } catch { /* swallow */ }
    }, 10000);
    return () => clearInterval(id);
  }, []);

  /* ---- Auto-scroll to bottom when messages change ---- */
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  /* ---- Send ---- */
  const send = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body || sending) return;

    // Optimistic append
    const optimistic = {
      _id: `tmp-${Date.now()}`,
      sender: 'client',
      body,
      createdAt: new Date().toISOString(),
      __optimistic: true,
    };
    setMessages((m) => [...m, optimistic]);
    setDraft('');
    setSending(true);

    try {
      const res = await api.post('/fintech/messages', { body });
      const saved = res.data.message;
      setMessages((m) =>
        m.map((x) => (x._id === optimistic._id ? saved : x))
      );
    } catch (err) {
      console.error(err);
      // Roll back on failure
      setMessages((m) => m.filter((x) => x._id !== optimistic._id));
      setDraft(body);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send(e);
    }
  };

  /* ---- Group messages by day ---- */
  const groups = [];
  let currentDay = null;
  for (const m of messages) {
    const label = fmtDayLabel(m.createdAt);
    if (label !== currentDay) {
      groups.push({ type: 'day', label, key: `day-${label}-${m._id}` });
      currentDay = label;
    }
    groups.push({ type: 'msg', key: m._id, msg: m });
  }

  return (
    <div className="px-4 pt-5 pb-8 lg:px-8 lg:pt-8 max-w-3xl mx-auto">
      <PageHeader holderName={data?.accountHolderName || ''} />

      {/* Title */}
      <div className="mb-5">
        <h2 className="font-serif text-[24px] leading-tight text-[#1a2b3c]">
          Messages
        </h2>
        <p className="text-[13px] text-gray-500 mt-1">
          Chat with your support team
        </p>
      </div>

      {/* Chat card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col h-[calc(100vh-320px)] min-h-[420px] overflow-hidden">

        {/* Card header */}
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[#0b2740] text-white flex items-center justify-center shrink-0">
            <ChatIcon className="w-5 h-5" />
          </span>
          <div className="leading-tight">
            <div className="text-[14px] font-semibold text-[#1a2b3c]">FDIC Support</div>
            <div className="text-[12px] text-gray-500 mt-0.5">Support</div>
          </div>
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#fafbfc]"
        >
          {loading && (
            <div className="h-full flex items-center justify-center text-[13px] text-gray-500">
              Loading…
            </div>
          )}

          {!loading && messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <span className="w-12 h-12 rounded-full bg-[#0b2740]/5 text-[#0b2740] flex items-center justify-center mb-3">
                <ChatIcon className="w-6 h-6" />
              </span>
              <div className="text-[14px] font-semibold text-[#1a2b3c]">
                No messages yet
              </div>
              <p className="text-[12px] text-gray-500 mt-1">
                Send a message to your support team to get started.
              </p>
            </div>
          )}

          {!loading &&
            groups.map((item) =>
              item.type === 'day' ? (
                <div key={item.key} className="flex justify-center my-2">
                  <span className="text-[10px] tracking-[0.14em] uppercase text-gray-400 bg-white border border-gray-100 px-2.5 py-1 rounded-full">
                    {item.label}
                  </span>
                </div>
              ) : (
                <Bubble key={item.key} msg={item.msg} />
              )
            )}
        </div>

        {/* Composer */}
        <form
          onSubmit={send}
          className="border-t border-gray-100 px-3 py-3 flex items-center gap-2 bg-white"
        >
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type your message..."
            className="flex-1 bg-white border border-gray-200 rounded-full px-4 py-2.5 text-[13px] text-[#1a2b3c] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0b2740]/20 focus:border-[#0b2740]"
          />
          <button
            type="submit"
            disabled={!draft.trim() || sending}
            aria-label="Send message"
            className="w-11 h-11 rounded-full bg-[#3b82f6] hover:bg-[#2563eb] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
          >
            <SendIcon className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default FintechMessages;